import React from 'react';

import type { Dispensaries } from 'src/gql/graphql';
import MediaQuery from 'shared/components/media-query';
import useUI from 'src/hooks/use-ui';
import { useIsConciergeEnabled } from 'src/concierge/use-is-concierge-enabled';
import { DesktopHeader } from './desktop-header';
import { MobileHeader } from './mobile-header';
import { ConciergeEntryBanner } from './concierge-entry-banner';
import { InfoBar } from './info-bar';
import { InfoBarMq } from './header.styles';
import { LoginNudgeProvider } from './login-nudge/login-nudge-context';

type NavigationProps = {
  dispensary: Dispensaries;
  isAccountEnabled: boolean;
};

export const Navigation = ({ dispensary, isAccountEnabled }: NavigationProps): JSX.Element => {
  const UI = useUI();
  // ECOMM-12462: on embedded (iframe) menus, the header's own ConciergeNavButton
  // pill only renders in DesktopHeader (>=880px) — MobileHeader has no
  // equivalent. This full-width banner fills that gap at the same breakpoint
  // where the pill disappears, embedded only (storefront keeps the FAB at that
  // breakpoint, unchanged). Reuses the exact same gate as the header pill
  // (conciergeChannels.web, kiosk exclusion, embedded-access rollout flag) plus an
  // explicit isEmbedded check so it never renders for dutchieMain/messaging/
  // mobileEcomm, which also fall through to this same Navigation component.
  //
  // The additional `!UI.isStoreFront` matters on its own, separate from
  // `useIsConciergeEnabled`'s own kiosk exclusion: `isStoreFront` and
  // `isEmbedded` are NOT mutually exclusive either — `isEmbedded` is true
  // whenever `UI.isIframe` is true (window.self !== window.top), regardless of
  // URL. A /stores/... page that ends up loaded inside a raw iframe (not the
  // sanctioned /embedded-menu/ integration, but nothing stops it) has both
  // `isStoreFront` and `isEmbedded` true simultaneously — confirmed live.
  // Without this exclusion, `isConciergeEnabled` is already `true` via the
  // storefront branch alone (independent of the embedded-access rollout
  // flag), so the banner would render ungated by that flag on top of the
  // already-present FAB, on what is by URL a storefront page.
  const isConciergeEnabled = useIsConciergeEnabled(dispensary, UI);
  const showConciergeEntryBanner = isConciergeEnabled && UI.isEmbedded && !UI.isStoreFront;

  return (
    <>
      <LoginNudgeProvider>
        <MediaQuery mediaQuery='@media (min-width: 880px)'>
          <DesktopHeader dispensary={dispensary} isAccountEnabled={isAccountEnabled} />
        </MediaQuery>
        <MediaQuery mediaQuery='@media (max-width: 879px)'>
          <MobileHeader dispensary={dispensary} isAccountEnabled={isAccountEnabled} />
        </MediaQuery>
      </LoginNudgeProvider>

      <InfoBarMq>
        <InfoBar />
      </InfoBarMq>

      {/* Rendered after InfoBar (the "OPEN / Recreational" row) rather than
          inside the MobileHeader MediaQuery block above, so the banner sits
          beneath it instead of directly under the header — its own separate
          MediaQuery instance keeps it at the same <880px breakpoint. */}
      {showConciergeEntryBanner && (
        <MediaQuery mediaQuery='@media (max-width: 879px)'>
          <ConciergeEntryBanner cName={dispensary.cName ?? null} />
        </MediaQuery>
      )}
    </>
  );
};
